import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2, TrendingUp, TrendingDown, BarChart3, Mail, MessageCircle, ArrowUpDown, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

interface CampaignStat {
  campaign_name: string;
  letter_type: string;
  mailing_round: number;
  total_sent: number;
  attributed_responses: number;
  response_rate: number;
  avg_days_to_respond: number | null;
}

interface LetterTypeStat {
  letter_type: string;
  mailing_round: number;
  total_sent: number;
  attributed_responses: number;
  response_rate: number;
  avg_days_to_respond: number | null;
}

type SortField = "campaign_name" | "total_sent" | "attributed_responses" | "response_rate";

const LETTER_TYPE_COLORS: Record<string, string> = {
  postcard: "hsl(var(--chart-1))",
  insert: "hsl(var(--chart-2))",
  envelope: "hsl(var(--chart-3))",
  letter: "hsl(var(--chart-4))",
  "first-class": "hsl(var(--chart-5))",
  other: "hsl(var(--muted-foreground))",
};

function parseResponseDate(response: string | null): Date | null {
  if (!response) return null;
  const trimmed = response.trim();
  // Match MM/DD/YYYY or MM/DD/YY
  const match4 = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match4) return new Date(parseInt(match4[3]), parseInt(match4[1]) - 1, parseInt(match4[2]));
  const match2 = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
  if (match2) {
    const year = parseInt(match2[3]) + 2000;
    return new Date(year, parseInt(match2[1]) - 1, parseInt(match2[2]));
  }
  return null;
}

function daysBetween(d1: Date, d2: Date): number {
  return Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
}

export function CampaignAnalytics() {
  const [campaignStats, setCampaignStats] = useState<CampaignStat[]>([]);
  const [letterTypeStats, setLetterTypeStats] = useState<LetterTypeStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [roundFilter, setRoundFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortField, setSortField] = useState<SortField>("response_rate");
  const [sortAsc, setSortAsc] = useState(false);
  const [minSent, setMinSent] = useState("50");
  const [excludedCount, setExcludedCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const pageSize = 1000;

      // 1. Fetch all mailing history
      let allHistory: any[] = [];
      let from = 0;
      while (true) {
        const { data, error } = await supabase
          .from("mailing_history")
          .select("mailing_record_id, campaign_name, letter_type, mailing_round, mailed_date")
          .not("campaign_name", "is", null)
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allHistory = [...allHistory, ...data];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      // 2. Fetch responded records (excluding temp/missing/bad)
      const { data: allResponded, error: re } = await supabase
        .from("mailing_records")
        .select("id, response")
        .eq("mailing_status", "responded");
      if (re) throw re;

      // Filter out temp/missing/bad and parse dates
      const validResponses = new Map<string, Date>();
      let excluded = 0;
      for (const r of allResponded || []) {
        if (!r.response || /temp|missing|bad/i.test(r.response)) {
          excluded++;
          continue;
        }
        const d = parseResponseDate(r.response);
        if (d) validResponses.set(r.id, d);
        else excluded++;
      }
      setExcludedCount(excluded);

      // 3. Build per-record mailing timeline
      const recordMailings = new Map<string, any[]>();
      for (const h of allHistory) {
        if (!recordMailings.has(h.mailing_record_id)) {
          recordMailings.set(h.mailing_record_id, []);
        }
        recordMailings.get(h.mailing_record_id)!.push(h);
      }

      // 4. Attribute each response to the most recent mailing before response date
      const attributions = new Map<string, { campaign_name: string; letter_type: string; mailing_round: number; days: number }>();

      for (const [recordId, responseDate] of validResponses) {
        const mailings = recordMailings.get(recordId);
        if (!mailings) continue;

        let best: any = null;
        let bestDate: Date | null = null;

        for (const m of mailings) {
          if (!m.mailed_date) continue;
          const md = new Date(m.mailed_date + "T00:00:00");
          if (md <= responseDate && (!bestDate || md > bestDate)) {
            best = m;
            bestDate = md;
          }
        }

        if (best && bestDate) {
          attributions.set(recordId, {
            campaign_name: best.campaign_name,
            letter_type: best.letter_type || "other",
            mailing_round: best.mailing_round || 1,
            days: daysBetween(bestDate, responseDate),
          });
        }
      }

      // 5. Count total sent per campaign (for rate calculation)
      const sentMap = new Map<string, number>();
      for (const h of allHistory) {
        const key = `${h.campaign_name}|${h.letter_type}|${h.mailing_round}`;
        sentMap.set(key, (sentMap.get(key) || 0) + 1);
      }

      // 6. Count attributed responses per campaign
      const attrMap = new Map<string, { count: number; totalDays: number }>();
      for (const a of attributions.values()) {
        const key = `${a.campaign_name}|${a.letter_type}|${a.mailing_round}`;
        const existing = attrMap.get(key) || { count: 0, totalDays: 0 };
        existing.count++;
        existing.totalDays += a.days;
        attrMap.set(key, existing);
      }

      // 7. Build campaign stats
      const campaigns: CampaignStat[] = [];
      for (const [key, totalSent] of sentMap) {
        const [campaign_name, letter_type, roundStr] = key.split("|");
        const attr = attrMap.get(key);
        const attributed = attr?.count || 0;
        campaigns.push({
          campaign_name,
          letter_type,
          mailing_round: parseInt(roundStr),
          total_sent: totalSent,
          attributed_responses: attributed,
          response_rate: totalSent > 0 ? (attributed / totalSent) * 100 : 0,
          avg_days_to_respond: attributed > 0 ? Math.round(attr!.totalDays / attributed) : null,
        });
      }

      // 8. Build letter type stats
      const typeMap = new Map<string, LetterTypeStat>();
      for (const c of campaigns) {
        const tKey = `${c.letter_type}|${c.mailing_round}`;
        if (!typeMap.has(tKey)) {
          typeMap.set(tKey, {
            letter_type: c.letter_type,
            mailing_round: c.mailing_round,
            total_sent: 0,
            attributed_responses: 0,
            response_rate: 0,
            avg_days_to_respond: null,
          });
        }
        const ts = typeMap.get(tKey)!;
        ts.total_sent += c.total_sent;
        ts.attributed_responses += c.attributed_responses;
      }
      // Recalc rates and avg days per type
      const typeAttrMap = new Map<string, { count: number; totalDays: number }>();
      for (const a of attributions.values()) {
        const tKey = `${a.letter_type}|${a.mailing_round}`;
        const existing = typeAttrMap.get(tKey) || { count: 0, totalDays: 0 };
        existing.count++;
        existing.totalDays += a.days;
        typeAttrMap.set(tKey, existing);
      }
      for (const [key, ts] of typeMap) {
        ts.response_rate = ts.total_sent > 0 ? (ts.attributed_responses / ts.total_sent) * 100 : 0;
        const ta = typeAttrMap.get(key);
        ts.avg_days_to_respond = ta && ta.count > 0 ? Math.round(ta.totalDays / ta.count) : null;
      }

      setCampaignStats(campaigns);
      setLetterTypeStats(Array.from(typeMap.values()).sort((a, b) => b.response_rate - a.response_rate));
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  const minSentNum = parseInt(minSent) || 0;

  const filteredCampaigns = useMemo(() => {
    return campaignStats
      .filter((c) => {
        if (roundFilter !== "all" && c.mailing_round !== parseInt(roundFilter)) return false;
        if (typeFilter !== "all" && c.letter_type !== typeFilter) return false;
        if (c.total_sent < minSentNum) return false;
        return true;
      })
      .sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        if (typeof aVal === "string" && typeof bVal === "string") {
          return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        return sortAsc ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
      });
  }, [campaignStats, roundFilter, typeFilter, sortField, sortAsc, minSentNum]);

  const letterTypeChartData = useMemo(() => {
    return letterTypeStats
      .filter((t) => roundFilter === "all" || t.mailing_round === parseInt(roundFilter))
      .sort((a, b) => b.response_rate - a.response_rate);
  }, [letterTypeStats, roundFilter]);

  const totals = useMemo(() => {
    const sent = filteredCampaigns.reduce((s, c) => s + c.total_sent, 0);
    const resp = filteredCampaigns.reduce((s, c) => s + c.attributed_responses, 0);
    return { sent, resp, rate: sent > 0 ? (resp / sent) * 100 : 0 };
  }, [filteredCampaigns]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(false); }
  };

  const letterTypes = useMemo(
    () => [...new Set(campaignStats.map((c) => c.letter_type))].sort(),
    [campaignStats]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-3 text-muted-foreground">Analyzing campaign attribution...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Methodology note */}
      <Card className="border-dashed">
        <CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground">
            <strong>Attribution method:</strong> Each response is credited to the <em>most recent mailing sent before the response date</em>.
            {excludedCount > 0 && <> {excludedCount} responses excluded (Temp/Missing/Bad).</>}
          </p>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs font-medium text-muted-foreground">Total Pieces Sent</p>
            <p className="text-2xl font-bold tabular-nums">{totals.sent.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs font-medium text-muted-foreground">Attributed Responses</p>
            <p className="text-2xl font-bold tabular-nums">{totals.resp.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs font-medium text-muted-foreground">Overall Response Rate</p>
            <p className="text-2xl font-bold tabular-nums">{totals.rate.toFixed(2)}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs font-medium text-muted-foreground">Campaigns Tracked</p>
            <p className="text-2xl font-bold tabular-nums">{filteredCampaigns.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Letter Type Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-5 w-5" />
            Response Rate by Format &amp; Round (Attributed)
          </CardTitle>
          <CardDescription>
            Based on last-touch attribution — each response credited to the mailing most likely responsible.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {letterTypeChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={letterTypeChartData} layout="vertical" margin={{ left: 110, right: 60, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => `${v}%`} domain={[0, "auto"]} />
                <YAxis
                  type="category"
                  dataKey={(d: LetterTypeStat) => `${d.letter_type} (R${d.mailing_round})`}
                  tick={{ fontSize: 12 }}
                  width={105}
                />
                <Tooltip
                  formatter={(value: number, name: string, props: any) => {
                    const d = props.payload as LetterTypeStat;
                    return [
                      `${value.toFixed(2)}% (${d.attributed_responses} of ${d.total_sent.toLocaleString()})`,
                      "Response Rate",
                    ];
                  }}
                  contentStyle={{ fontSize: 13 }}
                />
                <Bar dataKey="response_rate" radius={[0, 4, 4, 0]}>
                  {letterTypeChartData.map((entry, i) => (
                    <Cell key={i} fill={LETTER_TYPE_COLORS[entry.letter_type] || "hsl(var(--muted-foreground))"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">No data for selected filters</p>
          )}
          <div className="flex flex-wrap gap-4 mt-4 justify-center">
            {letterTypeChartData.map((t) => (
              <div key={`${t.letter_type}-${t.mailing_round}`} className="text-xs text-muted-foreground">
                <span className="font-medium">{t.letter_type} R{t.mailing_round}:</span>{" "}
                {t.attributed_responses} / {t.total_sent.toLocaleString()}
                {t.avg_days_to_respond !== null && <> · ~{t.avg_days_to_respond}d avg</>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-xs">Mailing Round</Label>
              <Select value={roundFilter} onValueChange={setRoundFilter}>
                <SelectTrigger className="h-9 w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Rounds</SelectItem>
                  <SelectItem value="1">Round 1 Only</SelectItem>
                  <SelectItem value="2">Round 2 Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Letter Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-9 w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {letterTypes.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Min. Pieces Sent</Label>
              <Select value={minSent} onValueChange={setMinSent}>
                <SelectTrigger className="h-9 w-[130px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No Minimum</SelectItem>
                  <SelectItem value="50">50+</SelectItem>
                  <SelectItem value="100">100+</SelectItem>
                  <SelectItem value="200">200+</SelectItem>
                  <SelectItem value="500">500+</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaign Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-5 w-5" />
            Campaign Detail (Attributed)
          </CardTitle>
          <CardDescription>Click column headers to sort. Only campaigns with {minSentNum}+ pieces shown.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 pr-4 cursor-pointer hover:text-foreground text-muted-foreground" onClick={() => toggleSort("campaign_name")}>
                    <span className="flex items-center gap-1">Campaign <ArrowUpDown className="h-3 w-3" /></span>
                  </th>
                  <th className="pb-2 pr-4 text-muted-foreground">Type</th>
                  <th className="pb-2 pr-4 text-muted-foreground">Rnd</th>
                  <th className="pb-2 pr-4 cursor-pointer hover:text-foreground text-muted-foreground text-right" onClick={() => toggleSort("total_sent")}>
                    <span className="flex items-center gap-1 justify-end">Sent <ArrowUpDown className="h-3 w-3" /></span>
                  </th>
                  <th className="pb-2 pr-4 cursor-pointer hover:text-foreground text-muted-foreground text-right" onClick={() => toggleSort("attributed_responses")}>
                    <span className="flex items-center gap-1 justify-end">Responses <ArrowUpDown className="h-3 w-3" /></span>
                  </th>
                  <th className="pb-2 pr-4 cursor-pointer hover:text-foreground text-muted-foreground text-right" onClick={() => toggleSort("response_rate")}>
                    <span className="flex items-center gap-1 justify-end">Rate <ArrowUpDown className="h-3 w-3" /></span>
                  </th>
                  <th className="pb-2 text-muted-foreground text-right">Avg Days</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.map((c, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                    <td className="py-2 pr-4 font-medium">{c.campaign_name}</td>
                    <td className="py-2 pr-4">
                      <Badge variant="outline" className="text-xs">{c.letter_type}</Badge>
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">{c.mailing_round}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{c.total_sent.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {c.attributed_responses > 0 ? (
                        <span className="flex items-center justify-end gap-1">
                          <MessageCircle className="h-3 w-3 text-green-600 dark:text-green-400" />
                          {c.attributed_responses}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      <RateBadge rate={c.response_rate} />
                    </td>
                    <td className="py-2 text-right tabular-nums text-muted-foreground">
                      {c.avg_days_to_respond !== null ? (
                        <span className="flex items-center justify-end gap-1">
                          <Clock className="h-3 w-3" />{c.avg_days_to_respond}d
                        </span>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
                {filteredCampaigns.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No campaigns match filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Key Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-5 w-5" />
            Key Takeaways
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Insights campaigns={campaignStats} letterTypes={letterTypeStats} />
        </CardContent>
      </Card>
    </div>
  );
}

function RateBadge({ rate }: { rate: number }) {
  if (rate >= 2)
    return <span className="inline-flex items-center gap-1 font-semibold text-green-600 dark:text-green-400"><TrendingUp className="h-3 w-3" />{rate.toFixed(2)}%</span>;
  if (rate >= 1)
    return <span className="font-medium text-foreground">{rate.toFixed(2)}%</span>;
  if (rate > 0)
    return <span className="text-muted-foreground">{rate.toFixed(2)}%</span>;
  return <span className="text-muted-foreground/50">0%</span>;
}

function Insights({ campaigns, letterTypes }: { campaigns: CampaignStat[]; letterTypes: LetterTypeStat[] }) {
  const meaningful = campaigns.filter((c) => c.total_sent >= 80 && c.attributed_responses > 0);
  if (meaningful.length === 0) {
    return <p className="text-sm text-muted-foreground">Need more campaign data with attributed responses to generate insights.</p>;
  }

  const best = [...meaningful].sort((a, b) => b.response_rate - a.response_rate).slice(0, 3);
  const r1 = letterTypes.filter((t) => t.mailing_round === 1).sort((a, b) => b.response_rate - a.response_rate);
  const r2 = letterTypes.filter((t) => t.mailing_round === 2).sort((a, b) => b.response_rate - a.response_rate);

  const r1Resp = r1.reduce((s, t) => s + t.attributed_responses, 0);
  const r1Total = r1.reduce((s, t) => s + t.total_sent, 0);
  const r2Resp = r2.reduce((s, t) => s + t.attributed_responses, 0);
  const r2Total = r2.reduce((s, t) => s + t.total_sent, 0);

  return (
    <ul className="space-y-3 text-sm">
      <li className="flex items-start gap-2">
        <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
        <span>
          <strong>Top campaigns (80+ sent, attributed):</strong>{" "}
          {best.map((c) => `${c.campaign_name} R${c.mailing_round} (${c.response_rate.toFixed(2)}%, ${c.attributed_responses} responses)`).join("; ")}
        </span>
      </li>
      {r1.length > 0 && r1[0].attributed_responses > 0 && (
        <li className="flex items-start gap-2">
          <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
          <span>
            <strong>Round 1:</strong> {r1Resp} attributed responses from {r1Total.toLocaleString()} pieces ({r1Total > 0 ? ((r1Resp / r1Total) * 100).toFixed(2) : 0}%).
            Best format: <strong>{r1[0].letter_type}</strong> ({r1[0].response_rate.toFixed(2)}%, ~{r1[0].avg_days_to_respond}d avg).
          </span>
        </li>
      )}
      {r2.length > 0 && r2Resp > 0 && (
        <li className="flex items-start gap-2">
          <TrendingDown className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 shrink-0" />
          <span>
            <strong>Round 2:</strong> {r2Resp} attributed responses from {r2Total.toLocaleString()} pieces ({r2Total > 0 ? ((r2Resp / r2Total) * 100).toFixed(2) : 0}%).
            Best format: <strong>{r2[0].letter_type}</strong> ({r2[0].response_rate.toFixed(2)}%).
          </span>
        </li>
      )}
    </ul>
  );
}
